import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Sparkles, Search, Mountain, X, Send } from "lucide-react";
import { toast } from "sonner";

/**
 * <InviteToAdventureModal recipient={user} open onOpenChange />
 * Picks one of the sender's adventures, lets them write a note, and sends an
 * invite. Recipient must have open_to_invites=true (server enforces, but UI
 * also gates the button).
 */
export default function InviteToAdventureModal({ recipient, open, onOpenChange, onSent }) {
    const [adventures, setAdventures] = useState([]);
    const [selected, setSelected] = useState(null);
    const [message, setMessage] = useState("");
    const [search, setSearch] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!open) return;
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                // Fetch the current user's recent adventures
                const me = await api.get("/auth/me");
                const { data } = await api.get("/adventures", { params: { author_id: me.data.user_id, limit: 50 } });
                if (!cancelled) setAdventures(data || []);
            } catch {
                /* ignore */
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [open]);

    const filtered = adventures.filter((a) =>
        !search || a.title.toLowerCase().includes(search.toLowerCase())
    );

    const send = async () => {
        if (!selected) { toast.error("Pick an adventure"); return; }
        setSubmitting(true);
        try {
            await api.post("/invites", {
                adventure_id: selected.id,
                recipient_user_id: recipient.user_id,
                message: message.trim(),
            });
            toast.success(`Invite sent to ${recipient.name || "them"}`);
            setSelected(null);
            setMessage("");
            onOpenChange(false);
            onSent?.();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSubmitting(false);
        }
    };

    if (!open) return null;

    return (
        <div
            data-testid="invite-modal-backdrop"
            onClick={() => onOpenChange(false)}
            className="fixed inset-0 z-50 bg-midnight-pine/60 backdrop-blur-sm flex items-center justify-center p-4"
        >
            <div
                data-testid="invite-modal"
                onClick={(e) => e.stopPropagation()}
                className="bg-snowcap rounded-xl border border-ridge-line max-w-lg w-full max-h-[85vh] overflow-y-auto shadow-2xl"
            >
                <header className="px-5 py-4 border-b border-ridge-line flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-ember-orange" />
                    <h2 className="font-display font-black text-lg text-midnight-pine flex-1">Invite {recipient.name} on an adventure</h2>
                    <button onClick={() => onOpenChange(false)} className="p-1 text-slate-gray hover:text-granite" data-testid="invite-modal-close">
                        <X className="w-5 h-5" />
                    </button>
                </header>
                <div className="p-5 space-y-4">
                    <div>
                        <label className="text-xs font-bold uppercase tracking-widest text-slate-gray mb-1.5 block">Pick an adventure</label>
                        <div className="relative mb-2">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-gray" />
                            <input
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Search your adventures…"
                                data-testid="invite-search-input"
                                className="w-full pl-10 pr-3 py-2 rounded-md bg-alpine-mist border border-ridge-line text-sm focus:outline-none focus:border-ember-orange"
                            />
                        </div>
                        {loading ? (
                            <div className="h-32 rounded-md bg-alpine-mist animate-pulse" />
                        ) : filtered.length === 0 ? (
                            <div className="text-sm text-slate-gray italic text-center py-6">
                                {adventures.length === 0 ? "Share an adventure first to invite people." : "No adventures match that search."}
                            </div>
                        ) : (
                            <ul className="max-h-72 overflow-y-auto space-y-1.5" data-testid="invite-adventure-list">
                                {filtered.slice(0, 20).map((a) => (
                                    <li key={a.id}>
                                        <button
                                            type="button"
                                            data-testid={`invite-adv-${a.id}`}
                                            onClick={() => setSelected(a)}
                                            className={`w-full flex items-center gap-3 p-2 rounded-md text-left transition-all ${
                                                selected?.id === a.id ? "bg-ember-orange/10 border-2 border-ember-orange" : "border-2 border-transparent hover:bg-alpine-mist"
                                            }`}
                                        >
                                            {a.photos?.[0] || a.cover_photo ? (
                                                <img src={a.cover_photo || a.photos[0]} alt="" className="w-12 h-12 rounded-md object-cover shrink-0" />
                                            ) : (
                                                <div className="w-12 h-12 rounded-md bg-trail-tan flex items-center justify-center shrink-0"><Mountain className="w-5 h-5 text-midnight-pine" /></div>
                                            )}
                                            <div className="flex-1 min-w-0">
                                                <div className="font-bold text-sm text-midnight-pine line-clamp-1">{a.title}</div>
                                                <div className="text-[10px] text-slate-gray uppercase tracking-widest">{a.activity}{a.distance_km ? ` · ${a.distance_km} km` : ""}</div>
                                            </div>
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                    <div>
                        <label className="text-xs font-bold uppercase tracking-widest text-slate-gray mb-1.5 block">Add a note</label>
                        <textarea
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            rows={3}
                            maxLength={500}
                            placeholder="Want to join me next weekend?"
                            data-testid="invite-message-input"
                            className="w-full px-3 py-2 rounded-md bg-alpine-mist border border-ridge-line text-sm focus:outline-none focus:border-ember-orange resize-none"
                        />
                    </div>
                </div>
                <footer className="px-5 py-3 border-t border-ridge-line flex items-center gap-2 justify-end">
                    <button onClick={() => onOpenChange(false)} className="px-4 py-2 rounded-md border border-ridge-line text-granite font-bold text-sm hover:bg-alpine-mist">Cancel</button>
                    <button
                        data-testid="invite-send-btn"
                        onClick={send}
                        disabled={!selected || submitting}
                        className="inline-flex items-center gap-1 px-4 py-2 rounded-md bg-ember-orange text-white font-bold text-sm hover:bg-ember-dark disabled:opacity-50 transition-all"
                    >
                        <Send className="w-4 h-4" /> {submitting ? "Sending…" : "Send invite"}
                    </button>
                </footer>
            </div>
        </div>
    );
}
